/**
 * The Videos tab of the Production Line detail page (production-lines):
 * the line's own videos as poster cards that open in the Videos library,
 * or the wireframe empty state. Moved verbatim out of
 * ProductionLineDetailPage.tsx.
 */

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import type { WorkspaceCopy } from '../../merchant/copy.js';
import {
  formatDate,
  formatDuration,
  videoStatusLabel,
  type VideoItem,
} from '../../merchant/model.js';
import {
  ButtonLink,
  Card,
  EmptyState,
  Poster,
  Tag,
} from '../../merchant/ui/primitives.js';

export function ProductionLineVideosTab({
  copy,
  language,
  productionLineId,
  lineVideos,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly productionLineId: string;
  readonly lineVideos: readonly VideoItem[];
}): ReactNode {
  return (
    <div role="tabpanel" className="mj-ws-mt-20">
      {lineVideos.length > 0 ? (
        <>
          <div className="mj-ws-vgrid">
            {lineVideos.map((video) => (
              <LineVideoCard
                key={video.id}
                video={video}
                copy={copy}
                language={language}
                lineId={productionLineId}
              />
            ))}
          </div>
          <div className="mj-ws-mt-16">
            <ButtonLink
              to={`/videos/line/${encodeURIComponent(productionLineId)}`}
              variant="quiet"
              small
            >
              {copy.line.openInVideos}
            </ButtonLink>
          </div>
        </>
      ) : (
        <EmptyState title={copy.line.noLineVideos} body={copy.line.noLineVideosBody}>
          <Poster width={60} state="placeholder" />
        </EmptyState>
      )}
    </div>
  );
}

function LineVideoCard({
  video,
  copy,
  language,
  lineId,
}: {
  readonly video: VideoItem;
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly lineId: string;
}): ReactNode {
  const navigate = useNavigate();
  return (
    <Card as="article" className="mj-ws-vcard" padding={0}>
      <Poster
        state={
          video.status === 'generating' || video.status === 'card'
            ? 'generating'
            : video.status === 'failed'
              ? 'failed'
              : 'playable'
        }
        posterUrl={video.posterUrl}
        duration={formatDuration(video.durationSeconds)}
        label={`${video.aspect} · ${video.platform || copy.common.dash}`}
        ariaLabel={copy.videos.open(video.title)}
        onClick={() =>
          void navigate(
            `/videos/line/${encodeURIComponent(lineId)}?video=${encodeURIComponent(video.id)}`,
          )
        }
      />
      <div>
        <h3 className="mj-ws-h3">{video.title}</h3>
        <div className="mj-ws-muted mj-ws-xs">{video.direction}</div>
        <div className="mj-ws-vcard-meta">
          <Tag status={video.status}>{videoStatusLabel(video.status, copy)}</Tag>
          <span className="mj-ws-muted mj-ws-xs mj-ws-tnum">
            {formatDate(video.date, language)}
          </span>
        </div>
      </div>
    </Card>
  );
}
